import { keymap } from 'prosemirror-keymap';
import type { Mark, Node } from 'prosemirror-model';
import type { EditorState, Transaction } from 'prosemirror-state';
import { Plugin, PluginKey } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { isValidPhoneNumber, validateEmail } from '@core/common-legacy';
import { WysiwygShortcuts } from '@core/wysiwyg-legacy';

import type { LinkAttributes, LinkSchema } from './link-mark';
import { LinkMark } from './link-mark';
import { PmLinkService } from './link.service';
import type { LinkEditPluginEvents } from './types';
import { LinkEditBeforeOpen, LinkEditClose } from './types';
import type {
  EditorButton,
  OverlayPosition,
  ProsemirrorDispatch,
  RtPMPlugin,
} from '../../../editor/types';
import { EditorMode } from '../../../editor/types';
import { getToolbarPosition } from '../../../helpers/position';
import { metaKey } from '../../../helpers/utils';
import { TextNode } from '../../basic/nodes';
import { contextMenuCommand } from '../../context-menu/context-menu';

type InsertLinkData = {
  mark: Mark;
  node: Node;
  nodePosition: number;
};

const linkPluginKey = new PluginKey('linkPlugin');

enum LinkActions {
  Open = 'open',
  Close = 'close',
}

export const linkCommands = {
  open: (state: EditorState, dispatch: ProsemirrorDispatch, tooltipOrigin?: HTMLElement) => {
    contextMenuCommand.close(state, dispatch, true);
    dispatch(
      state.tr.setMeta(linkPluginKey, {
        type: LinkActions.Open,
        payload: tooltipOrigin,
      }),
    );
  },
  close: (state: EditorState, dispatch: ProsemirrorDispatch) => {
    contextMenuCommand.disableForcedState(state, dispatch);
    dispatch(
      state.tr.setMeta(linkPluginKey, {
        type: LinkActions.Close,
      }),
    );
  },
};

type LinkCommands = { type: LinkActions.Open; payload?: HTMLElement } | { type: LinkActions.Close };

export interface LinkCallbacks {
  setValue: (val: string) => void;
  remove: () => void;
  exit: () => void;
  open: () => void;
}

export interface LinkInput {
  openLinkTooltip: (
    position: OverlayPosition,
    initialValue: string | null,
    callbacks: LinkCallbacks,
  ) => void;
  openLinkInput: (
    position: OverlayPosition,
    initialValue: string | null,
    callbacks: LinkCallbacks,
  ) => void;
  close: () => void;
  isOpen: () => boolean;
  updatePosition: (position: OverlayPosition) => void;
}

export class LinkEditPlugin implements RtPMPlugin {
  public api: {
    linkService: PmLinkService<LinkSchema>;
  };

  public buttons = {
    toggleLink: <EditorButton>{
      icon: 'link',
      tooltipText: 'wysiwyg.link',
      tooltipShortcut: WysiwygShortcuts.Link(metaKey),
      isActive: (...args) => this.api.linkService.isActive(...args),
      run: (state, dispatch, eventOrigin, isContextMenu) => {
        const origin = isContextMenu ? undefined : eventOrigin;
        linkCommands.open(state, dispatch, origin);
      },
    },
  };

  public eventStream$ = new Subject<LinkEditPluginEvents>();

  private editorView: EditorView;
  private destroy$ = new Subject<void>();
  private linkCallbacks: LinkCallbacks = {
    setValue: (value: string) => this.setLink(value),
    remove: () => this.api.linkService.removeLink(this.editorView.state, this.editorView.dispatch),
    exit: () => {
      linkCommands.close(this.editorView.state, this.editorView.dispatch);
      this.editorView.focus();
    },
    open: () => {
      const [link] = this.getLinksInSelection(this.editorView.state);
      this.openLink(link.mark);
    },
  };

  private plugin = new Plugin({
    key: linkPluginKey,
    props: {
      handleClick: (view: EditorView, pos: number, event: MouseEvent) => {
        const node = view.state.doc.nodeAt(pos);
        if (!node) return false;

        const mark = node.marks.find(mark => LinkMark.is(mark));
        if (!mark) return false;

        event.preventDefault();
        if (event.button === 1) {
          this.linkCallbacks.open();
          return true;
        }

        const linkAttrs = <LinkAttributes>mark.attrs;
        this.linkInput.openLinkTooltip(
          getToolbarPosition(view),
          linkAttrs.href,
          this.linkCallbacks,
        );
        return true;
      },
    },
    filterTransaction: (tr: Transaction, state) => {
      const meta: LinkCommands | undefined = tr.getMeta(linkPluginKey);

      if (meta?.type === LinkActions.Open) {
        let position: OverlayPosition;
        if (meta.payload) {
          const rect = meta.payload.getBoundingClientRect();
          position = {
            top: rect.top,
            left: rect.left + rect.width / 2,
          };
        } else {
          position = getToolbarPosition(this.editorView);
        }

        const [firstLink] = this.getLinksInSelection(state);
        const initialValue = firstLink ? (<InsertLinkData>firstLink).mark.attrs.href : null;
        this.eventStream$.next(new LinkEditBeforeOpen());
        this.linkInput.openLinkInput(position, initialValue, this.linkCallbacks);
      }

      if (meta?.type === LinkActions.Close) {
        this.linkInput.close();
        this.eventStream$.next(new LinkEditClose());
      }

      return true;
    },
  });

  private keymap = keymap({
    ['Mod-k']: state => {
      this.api.linkService.isActive(state)
        ? this.api.linkService.removeLink(state, this.editorView.dispatch)
        : linkCommands.open(state, this.editorView.dispatch, undefined);
      return true;
    },
  });

  constructor(
    private scrollStream$: Observable<void>,
    private linkInput: LinkInput,
  ) {}

  public beforeInit(mode: EditorMode, schema: LinkSchema) {
    this.api = {
      linkService: new PmLinkService(schema.marks.link),
    };
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;

    if (EditorMode.Wysiwyg === mode) {
      this.afterInitEditor();
    }
  }

  public getNativeEditorPlugins() {
    return [this.plugin, this.keymap];
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private afterInitEditor() {
    this.scrollStream$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.linkInput.isOpen()) return;

      this.linkInput.updatePosition(getToolbarPosition(this.editorView));
    });
  }

  private setLink(value: string) {
    const { state, dispatch } = this.editorView;
    const { selection, schema } = this.editorView.state;
    const trimmed = value.trim();
    let href: string;
    const hasProtocol = /^([a-zA-Z0-9]*?):\/\//.test(trimmed);
    const hasTelPrefix = /^tel:/.test(trimmed);
    const hasMailToPrefix = /^mailto:/.test(trimmed);

    if (hasProtocol) {
      href = trimmed;
    } else if (isValidPhoneNumber(trimmed.replace('tel:', '').replaceAll(' ', ''))) {
      href = hasTelPrefix ? trimmed : `tel:${trimmed}`;
    } else if (!validateEmail(trimmed.replace('mailto:', ''))) {
      href = hasMailToPrefix ? trimmed : `mailto:${trimmed}`;
    } else {
      href = `https://${trimmed}`;
    }

    const linkAttrs = <const>{
      href,
      target: '_blank',
      rel: 'noopener',
    };

    const text = state.doc.textBetween(selection.from, selection.to);

    if (selection.empty || text.trim().length === 0) {
      const [firstLink] = this.getLinksInSelection(state);
      if (!firstLink) {
        const anchor = schema.text(trimmed, [schema.marks.link.create(linkAttrs)]);
        dispatch(state.tr.replaceWith(selection.from, selection.to, [anchor, schema.text(' ')]));
      } else {
        const { tr } = this.editorView.state;
        const from = firstLink.nodePosition;
        const to = firstLink.nodePosition + firstLink.node.text!.length;
        tr.removeMark(from, to, schema.marks.link);
        tr.addMark(from, to, schema.marks.link.create(linkAttrs));
        dispatch(tr);
      }
    } else {
      this.api.linkService.setLink(this.editorView.state, dispatch, linkAttrs);
    }
    this.editorView.focus();
  }

  private openLink(mark: Mark): void {
    const attrs = <LinkAttributes>mark.attrs;
    window.open(attrs.href, attrs.target, attrs.rel);
  }

  private getLinksInSelection(state: EditorState) {
    const { doc, selection } = state;
    let linkData: InsertLinkData[] = [];
    doc.nodesBetween(selection.from, selection.to, (node, nodePosition) => {
      if (!TextNode.is(node)) return true;

      const linkMark = node.marks.find(mark => LinkMark.is(mark));
      if (!linkMark) return false;

      linkData = [...linkData, { mark: linkMark, node, nodePosition }];
      return false;
    });

    return linkData;
  }
}
